import React from 'react';
import type { ComponentType } from 'react';
import { AlertTriangle, Check, ChevronDown, Loader2, X } from 'lucide-react';
import { cn } from '@components/lib/utils';
import { CountBadge } from '@components/ui/CountBadge';

/* ─────────────────────────────────────────────────────────────────────────
 *  BackgroundTasksTray — bottom-left persistent tray for in-progress AI /
 *  sync / processing tasks. Mirrors prototype's `.bg-tray` (left:84px /
 *  bottom:24px). NEVER right side — that's reserved for AI Drawer.
 *
 *  Collapsed: pill with running spinner + count + chevron
 *  Expanded:  vertical stack of task cards (newest on top)
 *
 *  Auto-hides when no tasks. Done tasks linger 5s then fade out.
 * ───────────────────────────────────────────────────────────────────────── */

export type TaskStatus = 'running' | 'done' | 'failed';

type IconComponent = ComponentType<{ className?: string }>;

export type BackgroundTask = {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  /** 0..100 — optional progress bar */
  progress?: number;
  /** Custom icon for done state (defaults to Check). */
  doneIcon?: IconComponent;
  /** Click handler — typically navigates to the result. */
  onOpen?: () => void;
  /** Show extra inline action button (e.g. "View draft"). */
  ctaLabel?: string;
};

type BackgroundTasksTrayProps = {
  tasks: BackgroundTask[];
  expanded: boolean;
  onToggle: () => void;
  onDismissTask?: (id: string) => void;
};

export function BackgroundTasksTray({
  tasks,
  expanded,
  onToggle,
  onDismissTask,
}: BackgroundTasksTrayProps) {
  if (tasks.length === 0) return null;

  const runningCount = tasks.filter((t) => t.status === 'running').length;
  const doneCount = tasks.filter((t) => t.status === 'done').length;
  const failedCount = tasks.filter((t) => t.status === 'failed').length;

  const summaryTitle =
    runningCount > 0
      ? `${runningCount} ${runningCount === 1 ? 'task' : 'tasks'} running`
      : failedCount > 0
      ? `${failedCount} task${failedCount === 1 ? '' : 's'} failed`
      : `${doneCount} ${doneCount === 1 ? 'task' : 'tasks'} complete`;

  const summaryDesc = tasks.length > 0 ? tasks[0].title : '';

  return (
    <div
      className="marble-tray-in fixed bottom-24 left-4 right-4 z-30 flex w-auto flex-col gap-2 md:bottom-6 md:left-21 md:right-auto md:w-80"
      role="region"
      aria-label="Background tasks"
    >
      {/* Expanded stack (above the bar) */}
      {expanded && (
        <div className="flex flex-col gap-2">
          {tasks.map((t) => (
            <TaskItem key={t.id} task={t} onDismiss={onDismissTask} />
          ))}
        </div>
      )}

      {/* Collapsed bar — always visible */}
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-left shadow-lg transition-all hover:border-marble-blue-300 hover:shadow-xl"
      >
        <SummaryIcon
          running={runningCount > 0}
          failed={failedCount > 0 && runningCount === 0}
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold text-foreground">
            {summaryTitle}
          </div>
          <div className="truncate text-xs text-muted-foreground">
            {summaryDesc}
          </div>
        </div>
        {tasks.length > 1 && (
          <CountBadge
            count={tasks.length}
            variant="primary"
            size="md"
            className="shrink-0"
          />
        )}
        <ChevronDown
          className={cn(
            'size-3.5 shrink-0 text-marble-bg-500 transition-transform',
            expanded && 'rotate-180',
          )}
        />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  Summary icon for the collapsed bar
 * ───────────────────────────────────────────────────────────────────────── */

function SummaryIcon({
  running,
  failed,
}: {
  running: boolean;
  failed: boolean;
}) {
  if (running) {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-marble-purple-200 to-marble-blue-400 text-white">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }
  if (failed) {
    return (
      <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-marble-red-50 text-marble-red-700">
        <AlertTriangle className="size-4" />
      </div>
    );
  }
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-marble-green-50 text-marble-green-700">
      <Check className="size-4" />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 *  TaskItem — one row in the expanded stack
 * ───────────────────────────────────────────────────────────────────────── */

function TaskItem({
  task,
  onDismiss,
}: {
  task: BackgroundTask;
  onDismiss?: (id: string) => void;
}) {
  return (
    <div
      className={cn(
        'marble-tray-item-in group flex items-center gap-3 rounded-xl border bg-card p-3 shadow-md transition-all',
        task.status === 'running' && 'border-border',
        task.status === 'done' &&
          'border-marble-green-100 hover:border-marble-green-300',
        task.status === 'failed' && 'border-marble-red-200',
      )}
    >
      <TaskIcon status={task.status} doneIcon={task.doneIcon} />

      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-bold text-foreground">
          {task.title}
        </div>
        {task.description && (
          <div className="truncate text-xs text-muted-foreground">
            {task.description}
          </div>
        )}
        {typeof task.progress === 'number' && task.status === 'running' && (
          <div className="mt-1.5 h-[3px] overflow-hidden rounded-full bg-marble-bg-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-marble-purple-400 to-marble-blue-500 transition-all"
              style={{ width: `${Math.min(100, Math.max(0, task.progress))}%` }}
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {task.status === 'done' && task.ctaLabel && (
          <button
            type="button"
            onClick={task.onOpen}
            className="rounded-md px-2 py-1 text-xs font-semibold text-marble-blue-500 hover:bg-marble-bg-50"
          >
            {task.ctaLabel}
          </button>
        )}
        {task.status === 'failed' && (
          <button
            type="button"
            className="rounded-md border border-marble-red-200 px-2 py-1 text-xs font-semibold text-marble-red-700 hover:bg-marble-red-50"
          >
            Retry
          </button>
        )}
        {onDismiss && (task.status === 'done' || task.status === 'failed') && (
          <button
            type="button"
            onClick={() => onDismiss(task.id)}
            aria-label="Dismiss task"
            className="rounded-md p-1 text-marble-bg-500 opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function TaskIcon({
  status,
  doneIcon: DoneIconProp,
}: {
  status: TaskStatus;
  doneIcon?: IconComponent;
}) {
  if (status === 'running') {
    return (
      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-marble-purple-200 to-marble-blue-400 text-white">
        <Loader2 className="size-3.5 animate-spin" />
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-marble-red-50 text-marble-red-700">
        <AlertTriangle className="size-3.5" />
      </div>
    );
  }
  const DoneIcon = DoneIconProp ?? Check;
  return (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-marble-green-50 text-marble-green-700">
      <DoneIcon className="size-3.5" />
    </div>
  );
}

export type { BackgroundTask as Task };
